import type { Language } from '@hemmafixa/ui';
import { useCallback, useEffect, useState } from 'react';

// Small UI preferences only (never tokens or personal data). Storage can be blocked, so every access is guarded.
const SIDEBAR_KEY = 'hemmafixa-sidebar-collapsed';

export function readFlag(key: string, fallback: boolean): boolean {
  try {
    const value = localStorage.getItem(key);
    return value === null ? fallback : value === 'true';
  } catch {
    return fallback;
  }
}

export function writeFlag(key: string, value: boolean) {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // storage blocked: the choice still applies for this visit
  }
}

/** Whether the desktop sidebar shows icons only. Remembered per browser. */
export function useSidebarCollapsed() {
  const [collapsed, setCollapsed] = useState(() => readFlag(SIDEBAR_KEY, false));

  const update = useCallback((next: boolean) => {
    setCollapsed(next);
    writeFlag(SIDEBAR_KEY, next);
  }, []);

  return { collapsed, setCollapsed: update };
}

const PANEL_KEY = 'hemmafixa-panel-collapsed';

/** Whether the right-hand details column is shrunk to a strip. Remembered per browser. */
export function usePanelCollapsed() {
  const [collapsed, setCollapsed] = useState(() => readFlag(PANEL_KEY, false));

  const update = useCallback((next: boolean) => {
    setCollapsed(next);
    writeFlag(PANEL_KEY, next);
  }, []);

  return { collapsed, setCollapsed: update };
}

const LANGUAGE_KEY = 'hemmafixa-language';

export function readLanguage(): Language {
  try {
    return localStorage.getItem(LANGUAGE_KEY) === 'en' ? 'en' : 'sv';
  } catch {
    return 'sv';
  }
}

/** Interface language, Swedish by default. Remembered per browser and set on `<html lang>`. */
export function useLanguage() {
  const [language, setLanguage] = useState(readLanguage);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const update = useCallback((next: Language) => {
    setLanguage(next);
    try {
      localStorage.setItem(LANGUAGE_KEY, next);
    } catch {
      // storage blocked: the choice still applies for this visit
    }
  }, []);

  return { language, setLanguage: update };
}
